import React, { useRef } from 'react';

// Hooks
import { useFocusTrap } from '@hooks/useFocusTrap';

// Navigation components
import NavLink from './NavLink';
import { NavItem } from '@components/layout/Header';

interface AnimatedNavLinkProps {
  href: string;
  external?: boolean;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  children: React.ReactNode;
  isButton?: boolean;
  index: number;
  visible: boolean;
}

const AnimatedNavLink: React.FC<AnimatedNavLinkProps> = ({ href, external, onClick, children, isButton, index, visible }) => {
  return (
    <div
      className="w-full transition-all duration-500"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(20px)',
        transitionDelay: visible ? `${index * 0.1}s` : `${(3 - index) * 0.05}s`,
      }}
    >
      <NavLink
        href={href}
        external={external}
        className={isButton ? "mt-6 inline-block" : "transition-colors duration-300 hover:text-accent"}
        onClick={onClick}
        isButton={isButton}
      >
        {children}
      </NavLink>
    </div>
  );
};

interface MobileNavProps {
  isOpen: boolean;
  navItems: NavItem[];
  onLinkClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
  menuButtonRef: React.RefObject<HTMLButtonElement>;
}

const MobileNav: React.FC<MobileNavProps> = ({ isOpen, navItems, onLinkClick, menuButtonRef }) => {
  const navContainerRef = useRef<HTMLDivElement | null>(null);

  useFocusTrap(isOpen, navContainerRef, menuButtonRef);

  return (
    <div
      ref={navContainerRef}
      className="fixed inset-0 bg-background/90 backdrop-blur-md md:hidden transition-opacity duration-300"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100vh',
        zIndex: 40,
        overflowY: 'auto',
        WebkitBackdropFilter: 'blur(8px)',
        backdropFilter: 'blur(8px)',
        opacity: isOpen ? 1 : 0,
        pointerEvents: isOpen ? 'auto' : 'none',
      }}
      aria-modal="true"
      role="dialog"
      aria-label="Mobile navigation menu"
      aria-hidden={!isOpen}
    >
      <div className="h-full w-full pt-24 px-5 flex flex-col items-center">
        <nav
          className="flex flex-col items-center space-y-8 text-center w-full max-w-screen-2xl mx-auto"
          aria-label="Mobile navigation"
        >
          {navItems.map((item, index) => (
            <AnimatedNavLink
              key={index}
              href={item.href}
              external={item.external}
              isButton={item.isButton}
              index={index}
              visible={isOpen}
              onClick={(e) => {
                if (item.onClick) item.onClick(e);
                onLinkClick(e);
              }}
            >
              <span className={`${item.isButton ? "" : "text-2xl"} font-medium`}>{item.label}</span>
              {item.icon && !item.isButton && (
                <item.icon className="inline-block ml-1 h-4 w-4" aria-hidden="true" />
              )}
            </AnimatedNavLink>
          ))}
        </nav>
      </div>
    </div>
  );
};

export default MobileNav;
